import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
  Box,
  Typography,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import Highlight from "react-highlight-words";
import { useNavigate } from "react-router-dom";
import _debounce from "lodash/debounce";
import useUser from "../../hooks/useUser";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const NavbarSearch = () => {
  const { globalAutoSearchUser, globalAutoSearchResults } = useUser();
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [highlightWords, setHighlightWords] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const [searchPayload, setPayload] = useState(currentPayload);

  const handleSearch = async (query) => {
    if (query) {
      const payloadValues = {
        ...searchPayload,
        filters: [
          {
            fieldName: "userName",
            fieldValue: query,
            op: "like",
            dataType: "string",
            condition: "and",
            isJoin: false,
          },
        ],
      };
      setIsSearching(true);
      try {
        const results = await globalAutoSearchUser(payloadValues);
        setHighlightWords([query]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setHighlightWords([]);
    }
  };

  const debouncedSearch = _debounce(handleSearch, 300);

  useEffect(() => {
    setSearchResults(globalAutoSearchResults);
  }, [globalAutoSearchResults]);

  // Handle input changes
  const handleInputChange = (event, newInputValue) => {
    if (event.type == "change") {
      setSearchValue(newInputValue);
      debouncedSearch(newInputValue);
    }
  };

  // Handle selection from autocomplete dropdown
  const handleOptionSelect = (event, option) => {
    console.log(option, "optionoptionoption");
    if (typeof option === "object") {
      setSearchValue("");
      setSearchResults([]);
      setSelectedValue(null);

      // Navigate to user details

      navigate(`/user/details?id=${option.radUserId}`);
    }
  };
  const handleClear = () => {
    setSearchValue("");
    setSearchResults([]);
  };

  return (
    <Box
      sx={{
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        borderRadius: "20px",
        border: "1.5px solid #2196f3", // Blue line border
        height: "40px",
        display: "flex",
        alignItems: "center",
        width: "100%",
        maxWidth: "700px",
        px: { xs: 1, md: 2 },
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Autocomplete
        freeSolo
        sx={{ flexGrow: 1, width: "100%" }}
        options={searchResults}
        getOptionLabel={(option) =>
          option.userName +
          " - " +
          option.fullName +
          " - " +
          option.mobileNo +
          " - " +
          option.emailId
        }
        inputValue={searchValue}
        value={selectedValue}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        loading={isSearching}
        componentsProps={{
          paper: {
            sx: {
              width: "100%",
              minWidth: { xs: "300px", md: "500px", lg: "700px" },
              maxWidth: { xs: "95vw", md: "100%" },
              marginTop: "8px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              zIndex: 1500,
            },
          },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.radUserId}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "12px 16px", // Increased padding
              gap: 2,
              borderBottom: "1px solid rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                flexBasis: { xs: "50%", md: "20%" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#333", // Dark color for readability
                fontWeight: 600,
              }}
            >
              <Highlight
                searchWords={highlightWords}
                autoEscape={true}
                textToHighlight={option.userName || ""}
              />
            </Typography>
            <Typography
              variant="body2"
              sx={{
                flexBasis: { xs: "50%", md: "20%" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#666",
              }}
            >
              <Highlight
                searchWords={highlightWords}
                autoEscape={true}
                textToHighlight={option.fullName || ""}
              />
            </Typography>

            <Typography
              variant="body2"
              sx={{
                flexBasis: "25%",
                display: { xs: "none", md: "block" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#666",
              }}
            >
              <Highlight
                searchWords={highlightWords}
                autoEscape={true}
                textToHighlight={option.mobileNo || ""}
              />
            </Typography>
            <Typography
              variant="body2"
              sx={{
                flexBasis: "40%",
                display: { xs: "none", md: "block" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#666",
              }}
            >
              <Highlight
                searchWords={highlightWords}
                autoEscape={true}
                textToHighlight={option.emailId || ""}
              />
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search Users"
            sx={{
              "& .MuiInputBase-root": {
                color: "#333",
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(0, 0, 0, 0.5)",
                opacity: 1,
                fontSize: "15px",
                fontWeight: 500,
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(0, 0, 0, 0.5)" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isSearching ? (
                    <CircularProgress color="inherit" size={20} sx={{ color: "#333" }} />
                  ) : null}
                  {searchValue && (
                    <InputAdornment
                      position="end"
                      onClick={handleClear}
                      sx={{ cursor: "pointer", color: "rgba(0, 0, 0, 0.5)" }}
                    >
                      <ClearIcon />
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            fullWidth
          />
        )}
      />
    </Box>
  );
};

export default NavbarSearch;
